import { useMemo } from "react";
import * as R from "ramda";

import { Field } from "types/entities";

import resourceOperations from "./resourceOperations";

const operation = resourceOperations({
  basePath: "/api/fields",
  baseKey: ["fields"],
});

export const useField = (id: number, opts = {}) => operation.useRead<Field>(id, opts);

// TODO: Back this with the entities fields data?
// Is there a way for this to be like a sparse map for the fields?
// I want ot be able to useField(key) || useFields(keys) or useFields(queryOverEntities => keys)
// and for the cache to be shared between them, so if it's already in the cache then it doesn't need to be fetched again
// and it could call an endpoint like /api/fields?keys[]=key1&keys[]=key2
// so basically the keys are always just for the individual fields not
// the whole collection of fields
export const useFields = <T = Field[]>(opts = {}) => operation.useRead<T>(null, opts);

const selectFieldsBy = (attr: string, values: any[], data: Field[], filter = R.identity) => {
  const filteredFields = filter(
    R.filter((field: Field) => R.includes(R.prop(attr, field) as any, values) as boolean, data),
  ) as Field[];
  const fieldsByAttr = R.indexBy(R.prop(attr), filteredFields);

  return R.pipe(
    R.map((id: string) => fieldsByAttr[id]),
    R.reject(R.isNil),
  )(values);
};

// TODO: Don't pull all fields, just the ones we need
export const useFieldsByID = (ids: number[] = [], opts = {}) =>
  useFields({
    ...opts,
    select: (data: Field[]) => selectFieldsBy("id", ids, data),
  });

// TODO: Don't pull all fields, just the ones we need
export const useFieldsByIdentifier = (
  identifiers: string[] = [],
  { select = R.identity, ...opts } = {},
) =>
  useFields({
    ...opts,
    select: (data: Field[]) => selectFieldsBy("identifier", identifiers, data, select),
  });

// TODO: response should just include children by default
export const useFieldWithChildren = (id: number, opts = {}) => {
  const { data: field, ...queryProps } = useField(id, opts);
  const { data: children = [] } = useFieldsByID(field?.child_ids, {
    enabled: field && field.child_ids && field.child_ids.length > 0,
  });

  const fieldWithChildren = useMemo(() => {
    if (!field) return null;
    if (field.child_ids?.length && !children.length) return null;
    return R.assoc("children", children, field);
  }, [children, field]);

  return { ...queryProps, data: fieldWithChildren };
};

export const useFieldByKey = (key: string, opts = {}) =>
  useFields<Field>({
    ...opts,
    select: R.find(R.propEq(key, "key")),
  });

export const useFieldBy = (q: object, opts = {}) =>
  useFields<Field>({
    ...opts,
    select: R.find(R.where(q)),
  });

export const useFieldsBy = (q: object, opts = {}) =>
  useFields({
    ...opts,
    select: R.filter(R.where(q)),
  });

export const useFieldsByKey = (keys: string[] = [], opts = {}) =>
  useFields({
    ...opts,
    select: R.filter((field: Field) => keys.includes(field.key)),
  });

export const toKey = (kerOrIdentifier: string): string => kerOrIdentifier.split(":")[1];
export const isAdminOnly = R.propEq("admin", "required_role");
